import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { Input, Form } from "antd";
import { UserOutlined, LockOutlined, SafetyOutlined } from "@ant-design/icons";
import { SendPhoneCode } from "../../../SendPhoneCode";
import { FormInstance } from "../../../../hooks/createForm";
import { useTranslation } from "react-i18next";
import { RegisterInfo } from "@/types/user";
import { getPasswordValidate } from "@/utils/formRules";
import { PASSWORD_STRENGTH_TEXT_MAP } from "@/constants/enum";
import { PHONE_RULES } from "@/utils/formRules";
import { useDebounce } from "@/hooks/useDebounce";

import Axios from "axios";
import qs from "query-string";

import querystring from "query-string";

export interface PhoneRegisterFormProps
  extends React.HTMLAttributes<HTMLDivElement> {
  onSubmit?: () => void;
  onSuccess?: (user: RegisterInfo) => void;
  onFail?: (error: any) => void;
}

const InternalPhoneRegisterForm: ForwardRefRenderFunction<
  FormInstance,
  PhoneRegisterFormProps
> = ({ onSubmit, onSuccess, onFail }, ref) => {
  const [rawForm] = Form.useForm();
  const [phone, setPhone] = useState("");
  const { t } = useTranslation();

  const [isFind, setIsFind] = useState<boolean>(false);

  const passwordStrength = window.__config__.passwordStrength;

  useEffect(() => {
    const phone = querystring.parse(window.location.search).phone as string;

    if (typeof phone === "string") {
      setPhone(phone);
    }
  }, [rawForm]);

  const onFinish = async (values: any) => {
    try {
      // const authenticationClient = window.__authing__;
      await rawForm.validateFields();
      const { phone, code, password } = values;
      // const user = await authenticationClient.registerByPhoneCode(
      //   phone,
      //   code,
      //   password,
      //   {
      //     browser: navigator.userAgent,
      //     device: getOS(),
      //   },
      //   {
      //     generateToken: true,
      //   }
      // );
      onSuccess &&
        onSuccess({
          phone,
          code,
          password,
        });
    } catch (error) {
      // onFail && onFail(error);
    }
  };

  // 检查手机号是否已经被注册过了 by donglyc
  const handleCheckPhone = useDebounce(async (value: any) => {
    if (value.phone) {
      let { data } = await Axios.get(
        `/api/v2/users/find?${qs.stringify({
          userPoolId: window.__config__.userPoolId,
          key: rawForm.getFieldValue("phone"),
          type: "phone",
        })}`
      );
      setIsFind(data);
      rawForm.validateFields(["phone"]);
    }
  }, 1000);

  useImperativeHandle(ref, () => ({
    submit: async () => {
      try {
        onSubmit && onSubmit();
        await rawForm.validateFields();
        rawForm.submit();
      } catch (error) {
        onFail && onFail(error);
      }
    },
  }));

  return (
    <Form
      form={rawForm}
      onSubmitCapture={onSubmit}
      onFinish={onFinish}
      onValuesChange={handleCheckPhone}
    >
      <Form.Item
        name="phone"
        rules={[
          {
            required: true,
            message: t("login.inputPhone"),
          },
          {
            validator: (rule, value) => {
              if (value) {
                if (PHONE_RULES.test(value)) {
                  if (isFind) {
                    return Promise.reject(t("common.checkPhone"));
                  } else {
                    return Promise.resolve();
                  }
                } else {
                  return Promise.reject(
                    new Error(t("common.phoneFormateError"))
                  );
                }
              } else {
                return Promise.resolve();
              }
            },
          },
        ]}
      >
        <Input
          autoComplete="tel"
          onChange={(e) => {
            setPhone(e.target.value);
          }}
          size="large"
          placeholder={t("login.inputPhone")}
          prefix={<UserOutlined style={{ color: "#ddd" }} />}
        />
      </Form.Item>

      <Form.Item
        name="password"
        validateFirst
        rules={[
          ...getPasswordValidate(passwordStrength),
          {
            validator(_, value) {
              if (value.indexOf(" ") !== -1) {
                return Promise.reject(t("common.checkPasswordHasSpace"));
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <Input.Password
          autoComplete="password"
          size="large"
          visibilityToggle={false}
          placeholder={PASSWORD_STRENGTH_TEXT_MAP[passwordStrength].placeholder}
          prefix={<LockOutlined style={{ color: "#ddd" }} />}
        />
      </Form.Item>

      <Form.Item
        name="new-password"
        validateFirst
        dependencies={["password"]}
        rules={[
          ...getPasswordValidate(passwordStrength),
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue("password") === value) {
                return Promise.resolve();
              }
              return Promise.reject(new Error(t("common.checkPasswordDiff")));
            },
          }),
          {
            validator(_, value) {
              if (value.indexOf(" ") !== -1) {
                return Promise.reject(t("common.checkPasswordHasSpace"));
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <Input.Password
          autoComplete="new-password"
          size="large"
          visibilityToggle={false}
          placeholder={t("login.inputPwdAgain")}
          prefix={<LockOutlined style={{ color: "#ddd" }} />}
        />
      </Form.Item>

      <Form.Item
        style={{
          marginBottom: 0,
        }}
        name="code"
        validateFirst
        rules={[
          {
            required: true,
            message: t("login.inputFourMsgVerifyCode", {
              length: window.__config__.verifyCodeLength,
            }),
          },
          {
            validator: (_, value) => {
              if (value.length > window.__config__.verifyCodeLength) {
                return Promise.reject(
                  t("login.inputFourMsgVerifyCode", {
                    length: window.__config__.verifyCodeLength,
                  })
                );
              } else {
                return Promise.resolve();
              }
            },
          },
        ]}
      >
        <Input
          className={"authing-verify-code-input"}
          autoComplete="one-time-code"
          size="large"
          maxLength={window.__config__.verifyCodeLength}
          placeholder={t("login.inputFourMsgVerifyCode", {
            length: window.__config__.verifyCodeLength,
          })}
          prefix={<SafetyOutlined style={{ color: "#ddd" }} />}
          addonAfter={<SendPhoneCode phone={phone} />}
        />
      </Form.Item>
    </Form>
  );
};

export const PhoneRegisterForm = forwardRef<
  FormInstance,
  PhoneRegisterFormProps
>(InternalPhoneRegisterForm);
